/**
 * 계정 병력.
 *
 * 병사는 개체로 존재하고 죽으면 사라진다. 전투 밖의 모든 상태가 여기 모인다.
 * 시뮬레이션과 달리 결정론 제약이 없다. 다만 서버가 나중에 이 구조를 그대로
 * 들고 검증해야 하므로, 여기서도 부동소수점 상태는 만들지 않는다.
 */
import {
  Branch,
  COMPANION_DEPTHS,
  Culture,
  ELITE,
  GATE_EVERY,
  LEVEL_MAX,
  MAX_DEPTH,
  TROOP_BY_ID,
  levelFromOld,
  levelOf,
  companionsOf,
  eliteBody,
  specForTroop,
  RAID_BANDS,
  raidBand,
} from '../sim/troops.ts';
import { normalizeStash } from './stash.ts';
import { SUPPLIES, SUPPLY_BY_ID, SUPPLY_STOCK_MAX } from '../sim/supplies.ts';
import { MAX_SQUADS } from '../sim/commands.ts';
import { isDeploySlot } from '../sim/deploy.ts';
import { normalizeSkills } from './skill.ts';
import type { SkillState } from './skill.ts';
import { soldierName } from './names.ts';
import { createRealm, normalizeRealm, realmSoldiers } from './realm.ts';
import type { RealmState } from './realm.ts';
import type { Loadout } from '../sim/equipment.ts';
import type { CompanionDef, TroopDef } from '../sim/troops.ts';

/** 한 병사. uid는 계정 안에서 영원히 재사용되지 않는다. */
export interface Soldier {
  uid: number;
  /** 삼국시대풍 이름(`names.ts`). 계정 안에서 겹치지 않는다. 진급해도 그대로다. */
  name: string;
  troop: string;
  xp: number;
  /**
   * 잠근 병사. **자동 편성이 옮기지 않는다**(`autoPlan`) — 앉아 있던 부대에, 병영에 있었으면 병영에 그대로 둔다. 손으로
   * 옮기는 것은 막지 않는다. 플레이한 사람: "부대원을 잠글 수 있는 기능을 만들어줘. 잠긴 부대원은 자동편성을 눌러도
   * 다른곳으로 이동하지 않는거야." 잠그지 않았으면 칸이 없다(옛 저장 그대로).
   */
  locked?: true;
  /**
   * **용사**로 임명한 병사(`ELITE`, troops.ts). 장수가 돈을 내고 세운다(`appointElite`), 물릴 수 있다(`dismissElite`).
   * 전장에서 더 단단한 몸으로 서고, 이긴 판에서 쓰러져도 더 자주 돌아온다. 임명하지 않았으면 칸이 없다.
   */
  elite?: true;
}

/** 병사나 동료를 잠그거나 푼다(`Soldier.locked`·`CompanionState.locked`). */
export function setSoldierLock(s: { locked?: true }, on: boolean): void {
  if (on) s.locked = true;
  else delete s.locked;
}

export interface CompanionState {
  id: string;
  /** 이 출정 번호가 되기 전에는 나설 수 없다. 쓰러지면 늘어난다. */
  readyAt: number;
  /**
   * 데리고 나가 살아 돌아온 **전투** 수. 예전에는 단계가 여기서 나왔고, 지금은 레벨이 없던 저장을 옮길 때만 쓴다.
   *
   * 계정의 `sortieCount`와 단위가 다르다. 출정 한 번에 전투가 여럿이다. 이름을
   * `sorties`로 두었다가 고쳤다 — 같은 말로 다른 것을 세면 언젠가 더한다.
   *
   * 쓰러진 판은 세지 않는다. 업고 돌아온 것은 함께 싸운 것이 아니다.
   */
  battles: number;
  /**
   * 동료의 경험치 — 장수와 같이 **철수해 확정된 전과**다(`retreat`). 레벨은 장수와 같은 곡선(`heroLevelFor`).
   * 동료가 단계(전투 수)로만 크던 시절의 저장은 단계를 레벨 1·6·11·16(옛 눈금 1·4·7·10)으로 옮겨 채운다(`normalizeCompanions`).
   */
  xp: number;
  /** 수비 대장으로 선 땅(Todo9, `meta/garrison.ts`). 그동안 출정에 나서지 못한다(`availableCompanions`). */
  garrison?: string;
  /**
   * 잠근 동료. 병사의 잠금과 같다(`Soldier.locked`): 자동 편성이 맡은 부대·부관 자리·병영에서 옮기지 않는다. 플레이한
   * 사람: "부하장수도 부대원으로 있을때 잠글 수 있게 해줘."
   */
  locked?: true;
}

export interface Army {
  culture: Culture;
  gold: number;
  nextUid: number;
  soldiers: Soldier[];
  companions: CompanionState[];
  /** 지금까지 나간 출정 횟수. 동료 휴식을 세는 기준이다. */
  sortieCount: number;
  // 장수 레벨은 여기 저장하지 않는다. 누적 전과에서 나오는 값이라 저장해 두면
  // 두 출처가 생기고, 한쪽만 갱신되는 날 조용히 어긋난다. `heroLevelOf`를 쓴다.
  bestMerit: number;
  totalMerit: number;
  /**
   * 단신 토벌로 쌓은 장수 경험치. 장수 레벨에는 `RAID_XP_CAP`까지만 더해진다(`heroXpOf`).
   * 전과와 따로 두는 것은 전과가 순위이고 칭호이기 때문이다 — 토벌은 순위를 올리지 않는다.
   */
  raidXp: number;
  /**
   * 큰 도적 무리(`RAID_BANDS`의 1번부터)가 다시 모였는가. 큰 무리를 이기면 false가 되고, 출정에서 한 판을 이기면 다시
   * true다(`run.applyBattle`). 없으면(옛 저장) 모여 있는 것이다. 가장 작은 무리는 늘 있다(`raidBandsOpen`).
   */
  raidReady?: boolean;
  /**
   * 전투 성과(벤 적의 격, 친 횟수, 맞은 횟수)로 쌓은 장수 경험치(`heroDeedXp`). 전과처럼 철수해야 들어온다 — 장수가
   * 쓰러진 출정의 몫은 없다. 토벌은 주지 않는다(안전한 반복이 레벨을 올리면 안 된다, `raidXp`와 같은 까닭).
   */
  deedXp: number;
  /** 잃은 병사 누적. 영구 사망이 얼마나 무거운지 보여주는 숫자다. */
  fallen: number;
  /** 창고에 있는 장비. 착용한 것은 여기 없다. 같은 물건을 여러 개 가질 수 있다. */
  stash: string[];
  /** 누가 무엇을 차고 있는가. 키는 stash.ts의 HERO 또는 동료 식별자다. */
  loadouts: Record<string, Loadout>;
  /**
   * 마지막으로 짠 편성(분대별 병사 uid, 분대별 동료 id). 출정 화면을 다시 열면 이것으로 시작한다
   * (`restorePlan`). 없으면 자동 편성이다. 예전에는 화면을 열 때마다 자동 편성을 새로 돌려, 새 부하
   * 장수가 합류하면 짜 둔 편성이 통째로 바뀌었다(플레이한 사람이 짚었다).
   */
  formation?: { squads: number[][]; companions: (string | null)[]; escort?: string | null };
  /** 이겨 본 가장 깊은 깊이(토벌 빼고). 출발 깊이 지름길을 연다(`startDepths`). */
  deepest: number;
  /** 마지막으로 고른 출발 깊이. 다음 출정 화면이 이것으로 연다. */
  startDepth?: number;
  /**
   * 내 부대(장수의 부대)가 먼저 받을 병종. 자동 편성만 본다(`autoPlan`) — 전투 보정은 없다(장수의 지휘는 모든 부대에
   * 걸린다). 플레이한 사람: "내 장수부대의 선호 병종은 내가 지정할 수 있게 하자." 고르지 않았으면 칸이 없다.
   */
  heroBranch?: Branch;
  /**
   * 다음 출정의 시드(`nextRunSeed`). 출정 화면이 출발 깊이의 전장 특성(traits.ts)을 미리 보이려면 시드가 떠나기 전에
   * 정해져 있어야 한다. 그 출정의 첫 판을 정산할 때 비운다(`settleRunSeed`) — 떠날 때 비웠더니 첫 판 도중에 새로 고쳐
   * 출정을 버리면 다음 출정이 새 시드라 특성을 공짜로 다시 굴렸다(리뷰가 짚었다). 계정에 적어 두므로 화면을 다시
   * 열거나 새로 고쳐도 같다.
   */
  nextSeed?: number;
  /** 가진 군수품(supplies.ts) 수. 상점에서 사고(`buySupply`), 판을 정산할 때 쓴 만큼 준다(`applyBattle`). */
  supplies: Record<string, number>;
  /** 영토전의 세계 상태(Todo9, `realm.ts`). 영토전을 처음 열 때 만든다(`realmOf`) — 그 전의 저장에는 없다. */
  realm?: RealmState;
  /**
   * 마지막으로 고른 분대 출전 위치(Todo10, `sim/deploy.ts`의 칸 번호, 색인이 분대 번호, 고르지 않은 분대는 -1). 전투 준비
   * 화면이 이것으로 연다 — 판마다 다시 놓지 않게. 없으면 병종으로 정하는 기본 배치.
   */
  deploy?: number[];
  /** 장수 기술(Todo10, `meta/skill.ts`): 들고 나갈 기술과 기술마다의 레벨. 없으면 함성 레벨 1. */
  skills?: SkillState;
}

/** 이 계정의 영토전 세계. 없으면 처음 지도로 만들어 적는다. */
export function realmOf(army: Army): RealmState {
  if (!army.realm) army.realm = createRealm();
  return army.realm;
}

/** 다음 출정의 시드. 없으면 지금 정해 계정에 적는다. */
export function nextRunSeed(army: Army): number {
  if (typeof army.nextSeed !== 'number' || !Number.isInteger(army.nextSeed) || army.nextSeed < 0) {
    army.nextSeed = (Date.now() & 0x7fffffff) ^ (army.sortieCount * 7919);
  }
  return army.nextSeed;
}

/** 그 시드로 떠난 출정의 판을 정산했다 — 다음 출정은 새 시드다. 다른 시드의 출정(토벌 등)이면 그대로 둔다. */
export function settleRunSeed(army: Army, runSeed: number): void {
  if (army.nextSeed === runSeed) army.nextSeed = undefined;
}

/**
 * 출정을 시작할 수 있는 깊이: 1, 그리고 **넘은 관문 바로 다음 깊이**(6, 11, 16, 21, 26).
 *
 * 플레이한 사람이 "몇 뎁스 단위로 지름길, 10개 단위 어떤가, 더 좋은 개수가 있으면 그걸로"라고 했다. 5로 했다:
 * 관문(`isGate`)이 5마다 오고 적이 거기서 한 번 크게 몰려오며, 동료 합류(5·10·15)와 전리품(5부터)도 관문에 매여
 * 있다. 관문 다음 깊이는 곡선이 한숨 돌리는 자리라, 넘은 관문 바로 뒤에서 시작하면 첫 판이 그 구간에서 가장
 * 쉽다. 10으로 하면 첫 지름길이 두 번째 동료를 만난 뒤에야 열려, 가장 자주 되풀이하는 초반(1~10)을 줄이지 못한다.
 * 관문을 이겨 봐야 열리므로 동료는 이미 만났고, 건너뛴 깊이의 동료를 놓치는 일이 없다.
 */
export function startDepths(army: Army): number[] {
  const out = [1];
  for (let gate = GATE_EVERY; gate < MAX_DEPTH; gate += GATE_EVERY) {
    if (army.deepest >= gate) out.push(gate + 1);
  }
  return out;
}

/** 쓰러진 동료가 쉬는 출정 횟수. */
export const COMPANION_REST = 2;

/**
 * 분대 하나의 기본 정원(편장). 관등이 오르면 늘어난다(`squadSizeFor`) — 내 부대는 장수의 관등(`squadSize`), 부하
 * 부대는 맡은 동료의 관등(`mateSquadSize`)이다. 예전에는 모든 분대가 장수의 관등 하나를 썼다(Todo8).
 *
 * 예전에는 장수의 지휘력(90 → 210)이 한 번에 데려갈 수 있는 인원이었다. 아군을
 * 작게, 적을 크게 가기로 하면서 인원은 한동안 성장축에서 빠져 부대가 마흔여덟에서 멈췄다.
 * 플레이한 사람이 관등마다 조금씩 늘기를 바라, 지금은 관등이 인원의 성장축이다(동료 셋·상장군 96명 — 정원이 열둘이던 때는 88명).
 * 그 밖의 성장은 한 사람 한 사람의 레벨과 병종, 그리고 장수의 지휘다.
 *
 * **열넷이다(열둘이었다).** 적이 횡대로 오고 부대를 갖춘 뒤 시작 편성 열둘로 첫 관문(깊이 5)을 넘는 판이 절반쯤이었다
 * (QA, REPLAY 116: `first-gate` 45/90 — 기준은 80%). 같은 도구에서 도수와 창을 하나씩 더 세우면 76/90이다. 적 곡선을
 * 낮추지 않고 성장 수단으로 맞춘다는 방향대로, 플레이한 사람이 정했다(2026-10-07): 모든 관등의 정원을 +2
 * (편장 14 → … → 상장군 24). 편장만 올리면 첫 진급에서 정원이 늘지 않는다. 깊은 곳의 계단은 다시 재야 한다.
 */
export const SQUAD_SIZE = 14;

/**
 * 관등이 한 단계 오를 때마다 모든 분대의 정원이 이만큼 는다: 편장 14 → 비장 16 → … → 상장군 24.
 * 플레이한 사람이 "등급별로 지휘 인원이 조금씩 늘면 좋겠다"고 했고 +2로 정했다.
 *
 * 전장 규칙(`src/sim`)은 분대 인원을 세지 않는다 — 명부를 그대로 세운다. 그래서 리플레이 규칙은
 * 그대로이고, 서버가 대조할 것(명부의 분대 인원 ≤ 그 계정 관등의 정원)만 는다(docs/design.md).
 * 동료 셋에 상장군이면 96명이다. 가장 깊은 곳의 적(736)과 더해도 전장 정원(`MAX_UNITS` 1400) 안이다.
 */
export const RANK_SQUAD_BONUS = 2;

/** 누적 전과가 정하는 분대 정원. 관등은 전과만 보므로 토벌 경험치는 정원을 늘리지 않는다. */
export function squadSizeFor(totalMerit: number): number {
  return SQUAD_SIZE + RANK_SQUAD_BONUS * RANKS.indexOf(rankFor(totalMerit));
}

/** 이 계정의 지금 분대 정원 — 장수 직할대(내 부대)의 정원이다. 동료 분대는 `mateSquadSize`. */
export function squadSize(army: Army): number {
  return squadSizeFor(army.totalMerit);
}

/** 동료의 관등. 장수와 같은 표(`RANKS`)를 그 동료가 쌓은 경험치(철수해 확정된 전과, `CompanionState.xp`)로 읽는다. */
export function mateRank(army: Army, id: string): Rank {
  return rankFor(army.companions.find((c) => c.id === id)?.xp ?? 0);
}

/**
 * 동료가 맡는 분대의 정원. 그 동료의 관등(`mateRank`)이 정하고, 장수의 정원을 넘지 않는다 — 동료는 장수 휘하다.
 * 플레이한 사람이 청했다(Todo8): "지금은 플레이어 장수의 관직에 해당하는 병사 수를 모든 장수들이 같이 쓰고 있네.
 * 각 장수별(동료) 관직을 다르게 해줘. … 금방 들어온 동료는 12명 … 그 정도밖에 지휘 못하는거지." 막 합류한 동료는
 * 편장(`SQUAD_SIZE`)이다.
 */
export function mateSquadSize(army: Army, id: string): number {
  return Math.min(squadSize(army), squadSizeFor(army.companions.find((c) => c.id === id)?.xp ?? 0));
}

/**
 * 장수의 레벨. 누적 전과에서 나온다.
 *
 * 전과는 깊이 들어가 살아 돌아와야 쌓이므로, 장수의 성장은 안전한 반복이 아니라
 * 위험을 치른 값이 된다. 토벌이 전과를 안 주는 이유와 같다 — 토벌로 장수를 끝없이 키울
 * 수 있으면 순위가 반복 횟수의 경쟁이 된다. 토벌 경험치는 한도까지만 더한다(`RAID_XP_CAP`).
 *
 * 계정에 따로 적지 않는다. 적어 두면 전과와 두 출처가 되어, 한쪽만 갱신되는 날
 * 조용히 어긋난다. 옛 저장도 이 덕에 그대로 넘어온다 — 쌓아 둔 전과가 곧 레벨이다.
 */
export function heroLevelMerit(level: number): number {
  // 옛 곡선은 400·L·(L−1)이었다. 새 레벨 n은 옛 눈금의 m = 1 + 3(n−1)/5이고 두 배 느리게: 800·m·(m−1) = 32(3n+2)(3n−3).
  // (`levelXp`와 같은 모양이다 — troops.ts의 LEVEL_MAX 주석.) 전투 성과 경험치(`heroDeedXp`)가 생기며 1.5배로 올렸다:
  // 48(3n+2)(3n−3). 플레이한 사람이 청했다: "장수 경험치가 더 빨리 오를거야. 장수의 요구 경험치를 1.5배 증가 시켜줘."
  // 동료도 같은 곡선이다(`companionLevelOf`) — 동료도 성과 경험치를 받는다.
  return 48 * (3 * level + 2) * (3 * level - 3);
}

/**
 * 경험치 `from`에서 `to`까지 오른 레벨 수(소수). 레벨마다 요구 경험치가 달라 경험치 수로는 병사와 장수를 견줄 수 없다 —
 * 결과 화면의 전공 순위가 장수 +1,351과 병사 +24를 나란히 적자 "경험치 차이 이렇게 큰게 정상이야?"라는 물음이 나왔다
 * (둘 다 레벨의 절반쯤이었다). 그래서 "레벨 +0.5"처럼 적는다. `at`은 누적 경험치 → 그 레벨이 되는 경험치(곡선).
 */
export function levelsGained(from: number, to: number, at: (level: number) => number): number {
  const pos = (xp: number): number => {
    let l = 1;
    while (l < LEVEL_MAX && xp >= at(l + 1)) l++;
    if (l >= LEVEL_MAX) return LEVEL_MAX;
    return l + (xp - at(l)) / (at(l + 1) - at(l));
  };
  return Math.max(0, pos(to) - pos(from));
}

export function heroLevelFor(totalMerit: number): number {
  let l = 1;
  while (l < LEVEL_MAX && totalMerit >= heroLevelMerit(l + 1)) l++;
  return l;
}

/**
 * 토벌 경험치가 장수 레벨에 더해지는 한도. 옛 눈금 장수 레벨 5 몫(8000)과 같은 강함 — 새 눈금 레벨 8의 문턱이다
 * (`heroLevelMerit(levelFromOld(5))` — 요구 경험치 1.5배 뒤 26208, 그 전 17472). 처음에 16000(두 배)으로 적었더니 어느 레벨 문턱에도 닿지 않아 토벌만으로는
 * 레벨 7에서 멈췄다(리뷰가 짚었다).
 *
 * 처음에는 토벌이 장수를 전혀 키우지 않았다. 토벌로 장수를 키울 수 있으면 순위가 반복
 * 횟수의 경쟁이 되기 때문이다. 그런데 병사를 잃고 바닥에서 토벌을 도는 사람에게는
 * 싸워도 남는 것이 돈뿐이라 헛도는 느낌이었다(플레이한 사람이 짚었다). 그래서 쌓이게
 * 하되 한도를 둔다. 한도는 반복의 값을 묶는다.
 *
 * 한도가 거짓 주장까지 막지는 않는다. 처음에는 "서버는 토벌 기록을 몰라도 전과 + 한도까지
 * 받으면 된다"고 적었는데, 그러면 토벌 한 번 없이 `raidXp`를 8000으로 적어 레벨 5를 싣는다
 * (리뷰가 짚었다). 랭킹 서버는 토벌 리플레이를 검증해 이 값을 제 손으로 쌓아야 한다(design.md).
 */
export const RAID_XP_CAP = heroLevelMerit(levelFromOld(5));

/** 장수 레벨을 정하는 경험치: 누적 전과 + 한도까지의 토벌 경험치. */
export function heroXpOf(army: Army): number {
  return army.totalMerit + Math.min(army.raidXp, RAID_XP_CAP) + army.deedXp;
}

/**
 * 전투 성과 경험치. 플레이한 사람이 청했다: "전투 성과를 낸 경우(적을 처치, 적을 공격, 피격당함) 해당 병사(장수)에게
 * 추가 경험치를 주자." 수치는 재 보고 정했다(`defaultConfig`의 표준 편성, 맨몸, 입력 없음 = 기본 대형, 깊이 3·8·12·16마다
 * `CHECK_SEEDS` 앞 8개, 한 판 평균 — 장수가 벤 격 120·130·226·609, 친 7·11·19·37, 맞은 1·1·2·14; 전과 601·1175·2179·3988):
 * - 장수·동료: 벤 적의 격 × 2 + 친 횟수 × 5 + 맞은 횟수 × 5. 손 놓은 장수의 몫이 전과의 26~47%(깊이 3·8·12·16: 47·28·26·37%) — 요구 경험치를 1.5배로
 *   올린 것과 맞먹어, 앞장서 싸우는 장수는 전보다 빨리, 뒤에 선 장수는 전보다 늦게 오른다.
 * - 병사: 벤 적 × 8 + (친 횟수 + 맞은 횟수) × 2, 살아 돌아온 경험치(`battleXp`)까지만 — 잘 싸운 병사가 두 배까지 받는다.
 *   평균 병사는 한 판에 0.3~1.1명을 베고 1~6번 친다. 처음 값(4·1·1)의 두 배다 — 병사 경험치를 통째로 두 배로 올렸다
 *   (`battleXp`의 주석).
 */
export const DEED_XP = {
  heroKillPowerMul: 2,
  heroHit: 5,
  heroTaken: 5,
  soldierKill: 8,
  soldierHit: 2,
  soldierTaken: 2,
} as const;

export function heroDeedXp(killPower: number, hits: number, taken: number): number {
  return killPower * DEED_XP.heroKillPowerMul + hits * DEED_XP.heroHit + taken * DEED_XP.heroTaken;
}

export function soldierDeedXp(kills: number, hits: number, taken: number, depth: number): number {
  const raw = kills * DEED_XP.soldierKill + hits * DEED_XP.soldierHit + taken * DEED_XP.soldierTaken;
  return Math.min(raw, battleXp(depth));
}

export function heroLevelOf(army: Army): number {
  return heroLevelFor(heroXpOf(army));
}

/**
 * 관등. 이제 칭호다.
 *
 * 예전에는 관등마다 지휘력(데려갈 인원)이 붙어 있었다. 인원이 분대 정원으로
 * 고정되면서 관등은 장수 레벨이 어디쯤인지를 부르는 이름으로 남았다(뒤에 관등마다 분대 정원이 +2씩 붙었다).
 * 레벨 99로 바꾸며 문턱을 두 배로 올렸다(`RANKS`) — 쌓인 전과는 그대로라 옛 계정의 관등이 내려갈 수 있고, 그러면
 * 분대 정원이 줄어 넘치는 병사는 편성을 되살릴 때 병영으로 돌아간다(`restorePlan`).
 */
export interface Rank {
  readonly name: string;
  readonly merit: number;
}

/**
 * 문턱은 옛 문턱의 두 배다. 플레이한 사람이 "상장군 같은 계급도 마찬가지(두 배 느리게)"라고 했다. 쌓인 전과는
 * 그대로라 옛 계정의 관등이 내려갈 수 있다(경험치를 그대로 두기로 정했다).
 */
export const RANKS: readonly Rank[] = [
  { name: '편장', merit: 0 },
  { name: '비장', merit: 8000 },
  { name: '아장', merit: 24000 },
  { name: '부장', merit: 50000 },
  { name: '대장', merit: 90000 },
  { name: '상장군', merit: 150000 },
];

/**
 * **관등의 월급**(냥, `RANKS`와 같은 순서). 출정을 철수로 마칠 때 한 번 받는다(`salaryDue`, run.ts).
 *
 * 적장이 제 병사들과 함께 오게 고치자(`CHIEF_CHARGE`, world.ts) 적이 제 사기로 싸워 판마다 병사를 더 잃었고, 관문 20까지
 * 10번, 관문 30까지 13번이 걸렸다(기준 5~6번). 처음에는 적 곡선을 낮춰 맞추려 했다. 사용자가 짚었다: "전장이 점점
 * 위험해지는건 원래 당연한거고 성장으로 그 위험을 극복하게 하는게 좋지 않아?" — 곡선은 그대로 두고 성장 쪽을 올린다.
 * 노획을 고르게 1.5배로 올리면 중반(관문 15·20·25)이 풀렸다. 월급은 그 몫을 관등에 싣는다(사용자가 기준을 정할 때 낸
 * 생각이다: "관등에 따라 차례마다 월급"): 편장은 0이라 첫 관문 앞의 살림은 그대로이고, 관등이 오르는 중반부터 듣는다.
 */
export const RANK_SALARY: readonly number[] = [0, 300, 800, 1500, 2000, 2500];

/**
 * **그 관등의 월급을 받으려면 이겨야 하는 깊이**(`RANKS`와 같은 순서). 그 출정에서 이긴 가장 깊은 깊이가 이보다 얕으면
 * 그 깊이로 받을 수 있는 가장 높은 관등의 월급을 받는다(`salaryAt`) — 상장군이 깊이 8에서 이기고 돌아오면 비장의 월급이다.
 *
 * 처음에는 "최고 깊이에서 열 칸 안"(`SALARY_REACH`, run.ts)만 봤다. 그런데 관등은 영토전의 전과로도 오르고 최고 깊이는
 * 출정으로만 오른다 — 영토전으로 상장군이 된 계정은 최고 깊이가 얕아 깊이 1을 이기고 돌아오기만 해도 2,500냥을 받았다
 * (스무 번 연속 전승·손실 0, 한 번에 급료 46냥 — 리뷰가 재현했다). 최고 깊이를 일부러 낮게 두어도 지나갈 수 없게,
 * 관등마다 깊이를 건다. 값은 기준 플레이어가 그 관등이 되는 구간의 첫 깊이다(관문 10에 비장, 15에 아장, 20에 대장,
 * 25에 상장군으로 들어선다) — 전선에서 싸우는 계정은 늘 제 관등의 월급을 받는다. 깊이 5까지는 월급이 없다.
 */
export const SALARY_DEPTH: readonly number[] = [1, 6, 11, 16, 16, 21];

/** 누적 전과가 정하는 월급(그 관등의 온 몫). */
export function salaryFor(totalMerit: number): number {
  return RANK_SALARY[RANKS.indexOf(rankFor(totalMerit))] ?? 0;
}

/** 그 관등의 온 월급을 받으려면 이겨야 하는 깊이. */
export function salaryDepthFor(totalMerit: number): number {
  return SALARY_DEPTH[RANKS.indexOf(rankFor(totalMerit))] ?? 1;
}

/** 깊이 `wonDepth`에서 이긴 출정이 받는 월급: 제 관등까지에서, 그 깊이로 받을 수 있는 가장 높은 관등의 것. */
export function salaryAt(totalMerit: number, wonDepth: number): number {
  for (let k = RANKS.indexOf(rankFor(totalMerit)); k >= 0; k--) {
    if (wonDepth >= SALARY_DEPTH[k]) return RANK_SALARY[k];
  }
  return 0;
}

/** 누적 전과로 지금 관등을 찾는다. 오름차순이라 뒤에서부터 본다. */
export function rankFor(totalMerit: number): Rank {
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (totalMerit >= RANKS[i].merit) return RANKS[i];
  }
  return RANKS[0];
}

/** 다음 관등. 최고 관등이면 null. */
export function nextRank(totalMerit: number): Rank | null {
  for (const r of RANKS) if (totalMerit < r.merit) return r;
  return null;
}

/** 다음 문턱까지의 진행: 이번 구간에서 쌓은 값(`have`)과 구간 너비(`span`), 남은 값(`rest`). */
export interface Progress {
  readonly have: number;
  readonly span: number;
  readonly rest: number;
}

/**
 * 장수 지휘 줄의 두 막대. 레벨은 경험치(전과 + 한도까지의 토벌 경험치, `heroXpOf`), 관등은 **전과만**(`totalMerit`)으로
 * 잰다 — 토벌로는 레벨만 오르고 관등은 오르지 않는다. 끝까지 올랐으면 null. 화면에서 셈하면 둘을 섞기 쉬워 여기 둔다.
 */
export function heroProgress(army: Army): {
  level: (Progress & { readonly next: number }) | null;
  rank: (Progress & { readonly next: Rank; readonly size: number }) | null;
} {
  const lv = heroLevelOf(army);
  const xp = heroXpOf(army);
  const merit = army.totalMerit;
  const cur = rankFor(merit);
  const nr = nextRank(merit);
  return {
    level:
      lv < LEVEL_MAX
        ? {
            next: lv + 1,
            have: xp - heroLevelMerit(lv),
            span: heroLevelMerit(lv + 1) - heroLevelMerit(lv),
            rest: heroLevelMerit(lv + 1) - xp,
          }
        : null,
    rank: nr
      ? { next: nr, size: squadSizeFor(nr.merit), have: merit - cur.merit, span: nr.merit - cur.merit, rest: nr.merit - merit }
      : null,
  };
}
/**
 * 전투 하나를 버틴 병사가 얻는 경험치. 깊이가 깊을수록 많다.
 *
 * 깊이 한 칸에 +6이던 것을 +2로 줄였다. 깊이가 여섯 남짓에서 서른으로 늘어 한 출정의 전투 수가
 * 서너 판에서 열 판 넘게 됐다. +6 그대로면 깊이 10까지 한 번 다녀온 병사가 430을 얻는다 —
 * 예전 한 출정(깊이 4, 100)의 네 배다. +2면 210이다.
 *
 * **두 배로 올렸다(20 + 깊이 × 4, 밸런스 2).** 기준 플레이어의 계정을 되풀이해 재니(`npm run career`) 병사가 자라지
 * 못했다 — 출정마다 넷에 하나를 잃고 민병으로 채워, 관문 20에 들어설 때도 평균 2.5티어·레벨 10이었다. 3티어는 레벨 8
 * (누적 436), 4티어는 레벨 13(1,180)이 드는데 판마다 12~50을 받았다. 전투 성과 몫(`DEED_XP`)도 같이 두 배다. 부상
 * (`WOUNDED_PCT`)·적 곡선(`DEPTH_GROWTH_PCT`)과 함께 넣어야 성장 속도가 기준에 든다 — 경험치만 올리면 첫 관문만 당겨졌다.
 *
 * **첫 관문 너머는 깊이마다 +10이다(`DEEP_XP_FROM`·`DEEP_XP_STEP`).** 적장이 제 병사들과 함께 오게 고친 뒤(`CHIEF_CHARGE`,
 * world.ts) 후반이 막혔다 — 관문 30까지 13번, 계정 30개 가운데 20개만 넘었다. 돈을 올리는 것으로는(월급 `RANK_SALARY`,
 * 노획) 중반만 풀리고 후반은 그대로였다: 돈이 9만 냥까지 남았다. 후반의 병목은 병사의 질이다. 깊은 곳에서 싸운
 * 병사가 더 빨리 크게 한다 — 위험한 곳에서 살아 돌아온 값이다. 깊이 5까지는 예전과 같다: 깊이 1부터 올리면(20 + 깊이
 * × 10) 첫 관문을 한 번에 넘었다(기준 2~3번). 깊이 20에서 100 → 190, 30에서 140 → 290.
 */
const DEEP_XP_FROM = 5;
const DEEP_XP_STEP = 6;
export function battleXp(depth: number): number {
  return 20 + depth * 4 + Math.max(0, depth - DEEP_XP_FROM) * DEEP_XP_STEP;
}
/** 처치한 적의 격에서 나오는 노획. */
export function battleGold(killScore: number): number {
  return Math.floor(killScore / 3);
}

/**
 * 도적 두령의 목에 걸린 값.
 *
 * 잡은 만큼의 노획만으로는 부대를 다시 세우는 데 아홉 번 넘게 나가야 한다.
 * 그만큼 반복하는 것은 길이 아니라 형벌이다. 두령을 베면 한 번에 값이 되게
 * 해서, 서너 번이면 다시 설 수 있게 한다.
 *
 * 마무리를 보상하는 뜻도 있다. 두령을 베면 판이 끝나므로, 현상금이 없으면
 * 끝내지 않고 졸개만 오래 잡는 쪽이 이득이 된다.
 */
export const RAID_BOUNTY = 120;

/**
 * **전선의 폭**: 계정이 가 본 가장 깊은 곳(`Army.deepest`)에서 이 칸 안의 판이 "전선의 판"이다 — 지름길 두 구간.
 * 월급의 자격(`run.salaryFloor`)과 흩어진 도적 무리가 다시 모이는 조건(`raidBandsOpen`)이 같은 문턱을 쓴다.
 */
export const FRONT_REACH = GATE_EVERY * 2;

/** 전선의 판이 되려면 이 깊이 이상이어야 한다. 처음 하는 계정은 1이다. */
export function frontFloor(army: Army): number {
  return Math.max(1, army.deepest - FRONT_REACH + 1);
}

/** 고를 수 있는 도적 무리의 처지(`raidBandsOpen`). */
export interface RaidChoice {
  readonly band: number;
  readonly open: boolean;
  /** 닫힌 까닭(화면이 적는다). 열렸으면 빈 글. */
  readonly why: string;
}

/**
 * **지금 나설 수 있는 도적 무리**(`RAID_BANDS`, troops.ts). 무리의 색인이 곧 필요한 관등이다(1번은 비장부터 …).
 *
 * 큰 무리(1번부터)는 한 번 이기면 흩어지고, **전선의 출정에서 한 판을 이겨야** 다시 모인다(`Army.raidReady` — 가 본
 * 가장 깊은 곳에서 열 칸 안의 판, `frontFloor`. 월급의 자격과 같은 문턱이다). 처음에는 아무 깊이의 한 판이었는데, 다
 * 키운 부대로 깊이 1을 한 판 이기고 와 반란군을 다시 치기를 되풀이할 수 있었다 — 출정을 끼워 넣는 제한이 급료 한 번
 * 값의 통행료가 됐다(리뷰가 짚었고, 플레이한 사람이 조이기로 정했다 2026-10-09). 토벌은 급료도 없고
 * 병사도 걸지 않는데 큰 무리는 십여 초에 출정 한 번의 3분의 1을 번다 — 되풀이할 수 있으면 출정을 할 까닭이 사라진다
 * (플레이한 사람과 정한 것: "출정이 여전히 본업"). 출정을 **떠나기만** 하는 것으로는 풀리지 않는다 — 급료만 내고 곧장
 * 물러나 토벌을 되풀이하는 길이 된다. 지면 흩어지지 않는다(다시 덤빌 수 있다). 가장 작은 무리는 늘 열려 있다 — 부대를
 * 다 잃었을 때 일어서는 길이다.
 */
export function raidBandsOpen(army: Army): RaidChoice[] {
  const rank = RANKS.indexOf(rankFor(army.totalMerit));
  return RAID_BANDS.map((_, band) => {
    if (band === 0) return { band, open: true, why: '' };
    if (rank < band) return { band, open: false, why: `${RANKS[band]?.name ?? '더 높은 관등'}부터` };
    if (army.raidReady === false) {
      // 전선의 판을 이겨야 다시 모인다(`frontFloor`). 깊이를 적는다 — 문턱이 1이면(처음 하는 계정) 깊이를 뺀다.
      const floor = frontFloor(army);
      return { band, open: false, why: floor > 1 ? `흩어졌다 — 깊이 ${floor} 이상의 출정에서 한 판 이기면 다시 모인다` : '흩어졌다 — 출정에서 한 판 이기면 다시 모인다' };
    }
    return { band, open: true, why: '' };
  });
}

/** 이 무리에 나설 수 있는가. 모르는 번호는 가장 작은 무리로 본다(`raidBand`). */
export function canRaid(army: Army, band: number): boolean {
  return raidBandsOpen(army)[raidBand(band)]?.open === true;
}

const CULTURE_SLUG: Record<Culture, string> = {
  [Culture.Goguryeo]: 'go',
  [Culture.Baekje]: 'bj',
  [Culture.Silla]: 'sl',
};

export function createStartingArmy(culture: Culture): Army {
  const slug = CULTURE_SLUG[culture];
  const army: Army = {
    culture,
    // 예전 900냥은 백열두 명을 거느린 장수의 지갑이었다. 열넷이면 모집 몇 번과
    // 첫 장비 한두 점이 고민이 되는 정도가 맞다.
    gold: 400,
    nextUid: 1,
    soldiers: [],
    // **동료 없이 시작한다.** 이름을 가진 사람이 처음부터 셋 다 곁에 있으면
    // 만나는 일이 사라진다. 깊이를 넘을 때마다 하나씩 합류한다.
    companions: [],
    sortieCount: 0,
    bestMerit: 0,
    totalMerit: 0,
    raidXp: 0,
    deedXp: 0,
    deepest: 0,
    fallen: 0,
    // 맨몸으로 시작한다. 첫 상점 방문이 첫 결정이다.
    stash: [],
    loadouts: {},
    supplies: {},
  };
  // 열여섯. 직할대 하나(열넷)를 채우고 둘이 남는다 — 첫 전사자의 자리를 메울 사람이다. 정원이 열둘일 때는 열넷이었다
  // (도수 5·창 5·궁 1·민병 3) — 정원을 열넷으로 늘리며 도수와 창을 하나씩 더했다(`SQUAD_SIZE`). 이미 만든 계정은
  // 정원만 늘고 병사는 그대로다(모집으로 채운다).
  // 예전에는 백열두 명이었다. 이름을 부를 수 있는 수로 줄였다.
  //
  // **궁수는 하나다(도수 5·창 5·궁 1·민병 3).** 도수 4·창 4·궁 3이었다. 적이 횡대로 걸음을 맞춰 오게 된 뒤(Todo11)
  // 첫 관문(깊이 5)의 처음 도전의 이김이 고구려 계정 90개 가운데 69 → 41로 떨어졌다. 합성 부대로 견주니 궁수 셋을
  // 칼·창으로 바꾸면 크게 올랐다 — 한꺼번에 닿는 적 앞에서 칼·창 여덟이 버티지 못하는 것으로 본다(따로 재지는 않았다).
  // 궁수를 하나로 줄이면 계정에서 66/90이다(`npm run career`; 편성별 이김은
  // `npm run first-gate`). 적 곡선을 낮추는 대신 시작 편성을 고쳤다 — 플레이한 사람이 골랐다(docs/design.md "첫 관문과
  // 시작 편성"). 이미 만든 계정은 그대로다. 궁수는 모집으로 늘린다.
  const start: [string, number][] = [
    [`${slug}_blade2`, 6],
    [`${slug}_spear2`, 6],
    [`${slug}_bow2`, 1],
    [`${slug}_levy1`, 3],
  ];
  for (const [troop, n] of start) {
    for (let i = 0; i < n; i++) enlist(army, troop);
  }
  return army;
}

export function enlist(army: Army, troop: string, xp = 0): Soldier {
  const uid = army.nextUid++;
  const name = soldierName(army.culture, uid, takenNames(army));
  const s: Soldier = { uid, name, troop, xp };
  army.soldiers.push(s);
  return s;
}

function takenNames(army: Army): Set<string> {
  const set = new Set<string>();
  // 영토에 둔 병사(`Garrison.soldiers`)도 계정의 사람이다 — 거두면 돌아오므로 같은 이름을 새로 지으면 안 된다.
  for (const s of [...army.soldiers, ...realmSoldiers(army.realm)]) if (s.name) set.add(s.name);
  return set;
}

export function soldierById(army: Army, uid: number): Soldier | undefined {
  return army.soldiers.find((s) => s.uid === uid);
}

/**
 * 병사의 레벨. 경험치에서 나온다. 레벨을 따로 적지 않는 까닭은 장수 레벨과 같다.
 */
export function soldierLevel(s: Soldier): number {
  return levelOf(s.xp);
}

export function troopOf(s: Soldier): TroopDef {
  const t = TROOP_BY_ID.get(s.troop);
  if (!t) throw new Error(`없는 병종입니다: ${s.troop}`);
  return t;
}

/** 보유 병력을 병종별로 묶는다. 편성 화면이 이 형태로 읽는다. */
export interface TroopGroup {
  troop: TroopDef;
  soldiers: Soldier[];
  /** 진급할 수 있는 레벨에 이른 인원. */
  ready: number;
  /** 이 병종에서 경험치가 가장 많은 병사의 경험치. 다음 진급까지 얼마 남았는지 보인다. */
  bestXp: number;
  power: number;
}

export function groupByTroop(army: Army): TroopGroup[] {
  const map = new Map<string, Soldier[]>();
  for (const s of army.soldiers) {
    const list = map.get(s.troop);
    if (list) list.push(s);
    else map.set(s.troop, [s]);
  }
  const out: TroopGroup[] = [];
  for (const [troop, soldiers] of map) {
    const def = TROOP_BY_ID.get(troop);
    if (!def) continue;
    // 전장에 서는 몸(정병, 레벨 1)으로 잰다. 병종 표의 맨 값을 찍으면 출정 막대의
    // 편성 전투력(`selectionPower`)과 단위가 달라 한 화면에서 숫자가 어긋났다.
    const power = specForTroop(def.id, 1, true).power;
    out.push({
      troop: def,
      soldiers,
      ready: soldiers.filter((s) => canPromote(def, s)).length,
      bestXp: soldiers.reduce((m, s) => Math.max(m, s.xp), 0),
      power,
    });
  }
  // 티어 내림차순, 같은 티어면 가지 순서. 화면에서 강한 병력이 위로 온다.
  out.sort((a, b) => b.troop.tier - a.troop.tier || a.troop.branch.localeCompare(b.troop.branch));
  return out;
}

/** 계정 전체에서 지금 진급할 수 있는 병사 수. 결과 화면이 알려준다. */
export function promotableCount(army: Army): number {
  let n = 0;
  for (const s of army.soldiers) {
    const def = TROOP_BY_ID.get(s.troop);
    if (def && canPromote(def, s)) n++;
  }
  return n;
}

export function canPromote(def: TroopDef, s: Soldier): boolean {
  return def.upgradesTo.length > 0 && soldierLevel(s) >= def.levelToUpgrade;
}

/**
 * 진급. 레벨이 차고 돈이 있어야 한다.
 *
 * 경험치는 그대로 둔다. 예전에는 진급하면 0으로 돌아갔는데, 이제 경험치가 레벨의
 * 출처라 지우면 진급한 병사가 레벨 1로 떨어진다 — 더 센 병종이 되면서 더 약해진다.
 */
export function promote(army: Army, uid: number, toTroop: string): boolean {
  const s = soldierById(army, uid);
  if (!s) return false;
  const from = troopOf(s);
  if (!from.upgradesTo.includes(toTroop)) return false;
  if (!canPromote(from, s)) return false;
  const to = TROOP_BY_ID.get(toTroop);
  if (!to) return false;
  if (army.gold < to.upgradeCost) return false;
  army.gold -= to.upgradeCost;
  s.troop = toTroop;
  return true;
}

// ---------------------------------------------------------------------------
// 용사
// ---------------------------------------------------------------------------

/**
 * 관등마다 둘 수 있는 용사의 수(`RANKS`와 같은 순서). 플레이한 사람: "전체 8명정도인데, 주인공 관등이 올라감에따라
 * 임명가능한 수를 올리자." 마지막 값이 `ELITE.max`와 같아야 한다 — 판이 그보다 많은 용사를 세우지 않는다.
 */
export const ELITE_CAP: readonly number[] = [1, 2, 3, 5, 6, 8];

/** 용사의 급료(여느 최고 병종의 %). 더 단단한 몸의 값이다. */
export const ELITE_WAGE_PCT = 150;

/** 지금 관등으로 둘 수 있는 용사의 수. */
export function eliteCap(army: Army): number {
  const i = RANKS.indexOf(rankFor(army.totalMerit));
  return ELITE_CAP[Math.max(0, Math.min(i, ELITE_CAP.length - 1))];
}

/** 이 병사가 지금 용사인가 — 임명했고, 그 몸의 자격도 남아 있다(자격은 떨어지지 않지만 손댄 저장을 거른다). */
export function isElite(s: Soldier): boolean {
  return s.elite === true && eliteBody(s.troop, soldierLevel(s));
}

/** 계정의 용사 모두 — 병영·편성뿐 아니라 수비대·요양 중인 병사도 센다(자리를 비우려고 수비대에 넣어 두지 못하게). */
export function elitesOf(army: Army): Soldier[] {
  return [...army.soldiers, ...realmSoldiers(army.realm)].filter(isElite);
}

/** 이 병사를 용사로 세우는 값: 그 병종이 된 진급 값의 `ELITE.costMul`배. */
export function eliteCost(s: Soldier): number {
  return troopOf(s).upgradeCost * ELITE.costMul;
}

/**
 * 이 병사를 용사로 세울 수 있는가. `why`: 'tier'(최고 병종이 아니다) · 'level'(레벨이 모자란다) · 'full'(자리가 없다) ·
 * 'gold'(돈이 모자란다) · 'already'.
 */
export function eliteCheck(army: Army, s: Soldier): { ok: true } | { ok: false; why: 'tier' | 'level' | 'full' | 'gold' | 'already' } {
  if (s.elite === true) return { ok: false, why: 'already' };
  if (troopOf(s).tier < ELITE.tier) return { ok: false, why: 'tier' };
  if (soldierLevel(s) < ELITE.level) return { ok: false, why: 'level' };
  if (elitesOf(army).length >= eliteCap(army)) return { ok: false, why: 'full' };
  if (army.gold < eliteCost(s)) return { ok: false, why: 'gold' };
  return { ok: true };
}

/** 용사로 세운다. 돈을 낸다. 세웠으면 true. */
export function appointElite(army: Army, uid: number): boolean {
  const s = soldierById(army, uid);
  if (!s || !eliteCheck(army, s).ok) return false;
  army.gold -= eliteCost(s);
  s.elite = true;
  return true;
}

/** 용사에서 물린다. 낸 돈은 돌아오지 않는다 — 임명이 가벼운 결정이 되지 않게. */
export function dismissElite(army: Army, uid: number): boolean {
  const s = soldierById(army, uid);
  if (!s || s.elite !== true) return false;
  delete s.elite;
  return true;
}

/**
 * 새 병사를 모집한다. 잃은 자리를 메우는 비용이다.
 *
 * 25냥이던 것을 올렸다. 백 명을 굴릴 때는 한 명이 싸야 했지만, 열두 명짜리 분대의
 * 한 자리는 이름이 붙은 정병이다.
 */
export const RECRUIT_TROOP_COST = 40;

/**
 * **급료**(출정비, Todo6). 출정할 때 데려가는 병사마다 **먼저** 낸다 — 티어가 높을수록 비싸다(4티어 한 명에 들인 돈의
 * 약 3%). 짧게 치고 빠지면 손해, 건질 만큼 깊이 들어가야 이익이다. 초반(민병 14명 28냥)은 깊이 1 한 판(150냥 안팎)이면
 * 남는다. 정예 80명(약 920냥)은 깊이 10 안팎 두 판째에 본전.
 *
 * 선불인 까닭: 생환한 병사에게만 주면 "몇을 죽이면 급료를 안 줘도 된다"가 생긴다(플레이한 사람이 걱정했다). 먼저 내면
 * 죽음이 이득이 되는 길이 없다. 단신 토벌은 병사가 없으니 급료도 없다 — 파산한 부대가 다시 일어서는 길이다.
 * 색인이 티어(0은 비움).
 */
export const WAGE_BY_TIER: readonly number[] = [0, 2, 4, 8, 15];

/**
 * 급료를 낸다. 모자라면 아무것도 빼지 않고 null, 내면 낸 액수. 출정 한 번에 한 번만 부른다(`main.ts`의 출정 콜백).
 * 단신 토벌은 부르지 않는다(무료).
 */
export function payWage(army: Army, uids: readonly number[]): number | null {
  const wage = wageFor(army, uids);
  if (wage > army.gold) return null;
  army.gold -= wage;
  return wage;
}

/** 이 병사들을 데려가는 급료. 모르는 병사·병종은 세지 않는다. */
export function wageFor(army: Army, uids: readonly number[]): number {
  let n = 0;
  for (const uid of uids) {
    const s = soldierById(army, uid);
    const t = s ? TROOP_BY_ID.get(s.troop) : undefined;
    if (!s || !t) continue;
    const wage = WAGE_BY_TIER[t.tier] ?? WAGE_BY_TIER[WAGE_BY_TIER.length - 1];
    n += isElite(s) ? Math.floor((wage * ELITE_WAGE_PCT) / 100) : wage;
  }
  return n;
}

/**
 * 돈이 떨어졌을 때 공짜로 긁어모을 수 있는 인원.
 *
 * 한 명만 주면 안 된다. 첫 병사가 생기는 순간 조건이 풀려 다시 막히고, 남은
 * 것은 민병 하나로 백을 상대하는 판뿐이다. 나가나 마나인 출정을 한 번 더
 * 시키는 것은 길을 열어 준 것이 아니다. 얕은 곳에서 돈을 모아 다시 시작할
 * 만큼은 되어야 한다.
 */
// 직할대 하나를 채우는 수. 스물이던 것은 백 명 시절의 바닥이었다.
export const CONSCRIPT_FLOOR = SQUAD_SIZE;

/**
 * 바닥까지 떨어졌는가. 부대가 바닥이고 한 명 뽑을 돈도 없는 상태다.
 *
 * 이러면 아무것도 할 수 없다. 돈은 전투에서만 나오는데 병사가 없으면 나갈 수
 * 없고, 병사는 돈이 있어야 뽑는다. 저장을 지우는 것 말고는 길이 없는 막다른
 * 골목이다. 영구 사망은 무겁게 느껴져야 하지만, **무거운 것과 끝나는 것은
 * 다르다.** 부대를 잃는 벌은 이미 전과 0과 재건 비용으로 충분하다.
 *
 * 바닥선을 넘어서면 다시 값을 치른다. 공짜 병사가 계속 나오는 수도꼭지가
 * 되면 돈을 모을 이유가 사라진다.
 */
export function isDestitute(army: Army): boolean {
  return peopleCount(army) < CONSCRIPT_FLOOR && army.gold < RECRUIT_TROOP_COST;
}

/**
 * 계정의 사람 수: 병영의 병사 + 영토에 둔 병사(Todo9). 바닥 구제(공짜 징발)는 이것으로 센다 — 병영만 셌더니 수비대에
 * 보내 목록을 비우는 것만으로 공짜 징발이 다시 열려, 보내고 뽑고를 되풀이하면 돈 없이 사람이 불어났다(리뷰가 짚었다).
 */
export function peopleCount(army: Army): number {
  return army.soldiers.length + realmSoldiers(army.realm).length;
}

/**
 * 바닥에서는 공짜로 징발한다. 삼국시대에 사람을 긁어모으는 데 돈이 드는 것은
 * 무장과 훈련이지, 부르는 것 자체가 아니다.
 */
export function recruit(army: Army, culture: Culture): Soldier | null {
  const free = isDestitute(army);
  if (!free && army.gold < RECRUIT_TROOP_COST) return null;
  if (!free) army.gold -= RECRUIT_TROOP_COST;
  const slug = CULTURE_SLUG[culture];
  return enlist(army, `${slug}_levy1`);
}

/**
 * 정원까지 모자란 병사 수. 출정 화면이 "부족분 고용"에 쓴다. 계정에 이미 있는 병사는 출정에 넣지
 * 않았어도 센다 — 모자란 것은 사람이지 편성이 아니다.
 */
export function shortfall(army: Army, capacity: number): number {
  return Math.max(0, capacity - army.soldiers.length);
}

/**
 * n명을 차례로 뽑아 보면 몇 명이 되고 얼마가 드나. `recruit`를 n번 부른 것과 같은 셈이다 — 바닥에서
 * 공짜 징발이 섞이므로 "명 × 40냥"이 아니다. 화면이 따로 셈하면 한쪽만 고치는 날 값이 어긋난다.
 */
export function recruitPlan(army: Army, n: number): { count: number; cost: number } {
  let count = 0;
  let cost = 0;
  let gold = army.gold;
  // 공짜 징발은 `isDestitute`와 같은 셈(영토에 둔 병사까지)이어야 한다.
  let men = peopleCount(army);
  while (count < n) {
    const free = men < CONSCRIPT_FLOOR && gold < RECRUIT_TROOP_COST;
    if (!free && gold < RECRUIT_TROOP_COST) break;
    if (!free) {
      gold -= RECRUIT_TROOP_COST;
      cost += RECRUIT_TROOP_COST;
    }
    men++;
    count++;
  }
  return { count, cost };
}

/** n명까지 뽑는다. 돈이 떨어지면 멈춘다. 뽑은 수를 돌려준다. */
export function recruitUpTo(army: Army, culture: Culture, n: number): number {
  let count = 0;
  while (count < n && recruit(army, culture)) count++;
  return count;
}

/** 계정이 만난 동료. 아직 합류하지 않은 사람은 여기 없다. */
export function metCompanions(army: Army): CompanionDef[] {
  const defs = companionsOf(army.culture);
  return defs.filter((d) => army.companions.some((c) => c.id === d.id));
}

/**
 * 지금 나설 수 있는 동료.
 *
 * 만난 적 없는 사람은 나설 수 없다. 예전에는 상태가 없으면 나설 수 있는 것으로
 * 봤는데(`!st || ...`), 그때는 셋 다 처음부터 계정에 있었다. 합류를 만든
 * 지금은 그 기본값이 "아직 못 만난 사람도 데려간다"가 된다.
 */
export function availableCompanions(army: Army): CompanionDef[] {
  return metCompanions(army).filter((d) => {
    const st = army.companions.find((c) => c.id === d.id);
    // 영토의 수비 대장으로 선 동료(`garrison`)도 나서지 못한다.
    return st !== undefined && st.readyAt <= army.sortieCount && st.garrison === undefined;
  });
}

/** 합류. 이미 있으면 아무 일도 없다. */
export function joinCompanion(army: Army, id: string): boolean {
  if (army.companions.some((c) => c.id === id)) return false;
  if (!companionsOf(army.culture).some((d) => d.id === id)) return false;
  army.companions.push({ id, readyAt: 0, battles: 0, xp: 0 });
  return true;
}

/** 동료의 레벨. 모르는 동료·빈자리는 1. */
export function companionLevelOf(army: Army, id: string | null | undefined): number {
  if (!id) return 1;
  const st = army.companions.find((c) => c.id === id);
  return st ? heroLevelFor(st.xp) : 1;
}

/** 동료가 다음 레벨까지 더 벌어야 할 전과. 끝 레벨이면 0. */
export function companionXpToNext(army: Army, id: string): number {
  const st = army.companions.find((c) => c.id === id);
  const lv = st ? heroLevelFor(st.xp) : 1;
  return lv >= LEVEL_MAX ? 0 : heroLevelMerit(lv + 1) - (st?.xp ?? 0);
}

/** 편성 전체의 전투력. 출정 화면이 보여주는 숫자다. */
export function selectionPower(army: Army, uids: readonly number[]): number {
  let n = 0;
  for (const uid of uids) {
    const s = soldierById(army, uid);
    // 전장과 같은 몸으로 잰다: 레벨과 정병 보정까지.
    if (s) n += specForTroop(s.troop, soldierLevel(s), true, s.uid, isElite(s)).power;
  }
  return n;
}

// ---------------------------------------------------------------------------
// 군수품
// ---------------------------------------------------------------------------

/** 가진 군수품 수. */
export function supplyCount(army: Army, id: string): number {
  return army.supplies[id] ?? 0;
}

/** 군수품 하나를 산다. 돈이 모자라거나, 모르는 것이거나, 이미 상한(`SUPPLY_STOCK_MAX`)만큼 가졌으면 false. */
export function buySupply(army: Army, id: string): boolean {
  const def = SUPPLY_BY_ID.get(id);
  if (!def || army.gold < def.cost || supplyCount(army, id) >= SUPPLY_STOCK_MAX) return false;
  army.gold -= def.cost;
  army.supplies[id] = supplyCount(army, id) + 1;
  return true;
}

/** 군수품을 가진 것이 하나라도 있는가. 전투 준비 화면을 띄울지 가른다. */
export function hasAnySupply(army: Army): boolean {
  return SUPPLIES.some((s) => supplyCount(army, s.id) > 0);
}

/** 출전 위치는 Todo10 전의 저장에 없다. 칸 번호가 아닌 것은 -1(기본 배치)로. */
function normalizeDeploy(army: Army): void {
  const raw = (army as { deploy?: unknown }).deploy;
  if (!Array.isArray(raw)) {
    delete army.deploy;
    return;
  }
  army.deploy = Array.from({ length: MAX_SQUADS }, (_, sq) => (isDeploySlot(raw[sq]) ? (raw[sq] as number) : -1));
}

/** 군수품이 생기기 전의 저장에는 없다. 모르는 것·음수·정수가 아닌 것은 털어낸다. */
function normalizeSupplies(army: Army): void {
  const raw = (army as { supplies?: unknown }).supplies;
  const out: Record<string, number> = {};
  if (raw && typeof raw === 'object') {
    for (const s of SUPPLIES) {
      const n = (raw as Record<string, unknown>)[s.id];
      if (typeof n === 'number' && Number.isInteger(n) && n > 0) out[s.id] = Math.min(n, SUPPLY_STOCK_MAX);
    }
  }
  army.supplies = out;
}

// ---------------------------------------------------------------------------
// 저장
// ---------------------------------------------------------------------------

const SAVE_KEY = 'ildangbaek.army.v1';

/** 저장했으면 true. 전투 기록(`meta/replays.ts`)이 같은 한도를 나눠 쓰므로, 못 했으면 기록을 덜어 다시 해 본다. */
export function saveArmy(army: Army): boolean {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(army));
    return true;
  } catch {
    // 비공개 창이거나 저장이 막힌 경우. 이번 판은 그냥 진행한다.
    return false;
  }
}

export function loadArmy(): Army | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Army;
    if (!Array.isArray(parsed.soldiers) || typeof parsed.nextUid !== 'number') return null;
    // 나라는 진영 선택(Todo9)으로 고른다. 모르는 값이면 병종 접두·동료·전용 장비가 모두 어긋난다 — 새로 시작이 낫다.
    if (parsed.culture !== Culture.Goguryeo && parsed.culture !== Culture.Baekje && parsed.culture !== Culture.Silla) return null;
    // 병종이 사라졌거나 이름이 바뀐 저장은 버린다. 반쯤 깨진 군대보다 새로 시작이 낫다.
    if (parsed.soldiers.some((s) => !TROOP_BY_ID.has(s.troop))) return null;
    // 장비는 병종과 달리 없어졌다고 계정을 버릴 이유가 없다. 창고만 털어낸다.
    normalizeStash(parsed);
    normalizeCompanions(parsed);
    // 토벌 경험치가 생기기 전의 저장에는 없다. undefined가 Math.min을 타면 레벨이 NaN이 된다.
    if (!Number.isFinite(parsed.raidXp) || parsed.raidXp < 0) parsed.raidXp = 0;
    // 전투 성과 경험치가 생기기 전의 저장에는 없다.
    if (!Number.isFinite(parsed.deedXp) || parsed.deedXp < 0) parsed.deedXp = 0;
    // 가장 깊은 깊이를 적기 전의 저장에는 없다. 만난 동료 수로 아래를 잡는다 — 동료는 관문(5·10·15)을
    // 이겨야 오므로 둘을 만났으면 적어도 10은 이겼다. 그보다 깊이 간 것은 다시 이기면 적힌다.
    if (!Number.isFinite(parsed.deepest) || parsed.deepest < 0) {
      parsed.deepest = COMPANION_DEPTHS[parsed.companions.length - 1] ?? 0;
    }
    // 영토전(Todo9)이 생기기 전의 저장에는 없다 — 그대로 두면 처음 열 때 만든다. 있으면 땅 목록에 맞춘다. 이름보다 먼저
    // 맞춘다 — 수비대 병사도 이름을 채우고 겹침을 풀어야 해서(`normalizeNames`).
    if (parsed.realm !== undefined) parsed.realm = normalizeRealm(parsed.realm);
    reconcileGarrisons(parsed);
    // 잠금은 true이거나 없다 — 수비대·요양 중인 병사와, 방금 남의 땅에서 거둬 온 병사도 같다. 처음에는 병영의 병사만
    // 봐서, 손댄 저장의 수비 병사는 'yes'를 지닌 채 돌아와 카드에는 자물쇠가 뜨고 자동 편성은 잠금으로 치지 않았다(리뷰).
    for (const s of [...parsed.soldiers, ...realmSoldiers(parsed.realm)]) {
      if (s.locked !== true) delete s.locked;
      // 용사도 true이거나 없다. 자격 없는 병사에 붙은 것(손댄 저장)은 지운다.
      if (s.elite !== true || !eliteBody(s.troop, soldierLevel(s))) delete s.elite;
      // 옛 저장의 임무(`duty`, REPLAY 97~98)와 지킬 사람(`ward`, 99)은 지운다 — 용사는 제가 든 부대의 대장을 호위한다
      // (플레이한 사람, 2026-10-06). 옮길 것이 없다: 지킬 사람은 편성(앉은 부대)에서 나온다.
      delete (s as { duty?: unknown }).duty;
      delete (s as { ward?: unknown }).ward;
    }
    normalizeNames(parsed);
    normalizeSupplies(parsed);
    normalizeDeploy(parsed);
    // 내 부대의 선호 병종은 아는 병종이거나 없다.
    if (parsed.heroBranch !== undefined && !(Object.values(Branch) as string[]).includes(parsed.heroBranch)) delete parsed.heroBranch;
    normalizeSkills(parsed);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 옛 저장을 지금 모양으로 맞춘다.
 *
 * 동료 합류가 생기기 전의 저장에는 셋이 다 들어 있고 센 값이 없다. 그대로 두면
 * 센 값이 NaN을 타고 지휘 보정으로 들어갔다(단계 시절). 지금은 경험치가 같은 처지다. 모르는
 * 식별자는 털어낸다 — 문화를 바꾼 저장이 남의 동료를 들고 있을 수 있다.
 */
function normalizeCompanions(army: Army): void {
  // 지휘력이 누적 전과에서 나오기 전에는 계정에 적혀 있었다. 남겨 두면
  // `saveArmy`가 통째로 직렬화하므로 영원히 다시 저장된다. 지운다.
  delete (army as Army & { command?: number }).command;

  if (!Array.isArray(army.companions)) {
    army.companions = [];
    return;
  }
  const known = new Set(companionsOf(army.culture).map((d) => d.id));
  army.companions = army.companions
    .filter((c) => c && known.has(c.id))
    .map((c) => {
      // `sorties`로 부르던 시절의 저장도 받는다. 이름만 바뀌고 세는 것은 같다.
      const legacy = (c as { sorties?: number }).sorties;
      const n = Number.isFinite(c.battles) ? c.battles : Number.isFinite(legacy) ? legacy! : 0;
      // 레벨이 생기기 전의 저장: 그때의 단계(살아 돌아온 전투 0·5·12·25회)를 레벨 1·4·7·10으로 옮긴다. 지휘 보정이
      // 거의 같은 자리다(`companionCaptain` 주석). 세던 전투 수를 버리면 오래 키운 동료가 처음으로 돌아간다.
      const legacyGrade = n >= 25 ? 3 : n >= 12 ? 2 : n >= 5 ? 1 : 0;
      // 옛 단계 0–3은 옛 눈금 레벨 1·4·7·10의 지휘와 맞았다. 새 눈금으로 옮긴 레벨의 경험치를 준다(`levelFromOld`).
      const xp = Number.isFinite(c.xp) && c.xp >= 0 ? c.xp : heroLevelMerit(levelFromOld(1 + 3 * legacyGrade));
      return {
        id: c.id,
        readyAt: Number.isFinite(c.readyAt) ? c.readyAt : 0,
        battles: n,
        xp,
        ...(typeof c.garrison === 'string' ? { garrison: c.garrison } : {}),
        ...(c.locked === true ? { locked: true as const } : {}),
      };
    });
}

/**
 * 이름이 생기기 전의 저장에는 병사 이름이 없다. uid 순으로 채운다 — 같은 저장이면 늘
 * 같은 이름이 붙는다. 겹친 이름(손으로 고친 저장 등)도 여기서 풀어 준다.
 */
export function normalizeNames(army: Army): void {
  const taken = new Set<string>();
  // 영토에 둔 병사도 계정의 사람이다(Todo9) — 이름을 채우고 병영과 겹치지 않게 한다.
  const sorted = [...army.soldiers, ...realmSoldiers(army.realm)].sort((a, b) => a.uid - b.uid);
  for (const s of sorted) {
    if (typeof s.name !== 'string' || s.name.length === 0 || taken.has(s.name)) {
      s.name = soldierName(army.culture, s.uid, taken);
    }
    taken.add(s.name);
  }
}

/**
 * 수비대를 계정과 맞춘다(Todo9). 내 나라 땅이 아닌 곳의 수비대는 거둔다(병사는 목록으로). 계정 목록과 uid가 겹친 병사는
 * 목록 쪽을 남긴다 — 같은 사람이 두 곳에 있으면 안 된다. 대장은 만난 동료여야 하고, 동료의 `garrison` 표시는 실제로 대장인
 * 땅과 같아야 한다(한 동료가 두 땅의 대장이면 앞 땅만). 손으로 고친 저장이나 중간에 끊긴 저장도 열린다.
 */
export function reconcileGarrisons(army: Army): void {
  const realm = army.realm;
  for (const st of army.companions) delete st.garrison;
  if (!realm) return;
  const seen = new Set(army.soldiers.map((s) => s.uid));
  // 다친 병사(6단계)도 한 사람이 두 곳에 있으면 안 된다 — 병영 쪽을 남긴다.
  if (realm.infirmary) {
    realm.infirmary = realm.infirmary.filter((x) => {
      if (seen.has(x.soldier.uid)) return false;
      seen.add(x.soldier.uid);
      return true;
    });
    if (realm.infirmary.length === 0) delete realm.infirmary;
  }
  if (!realm.garrisons) return;
  const known = new Set(army.companions.map((c) => c.id));
  for (const id of Object.keys(realm.garrisons)) {
    const g = realm.garrisons[id];
    if (realm.owner[id] !== army.culture) {
      // 거두는 병사도 `seen`에 적는다 — 적지 않았더니 같은 uid가 다른 땅의 수비대에도 남아 한 사람이 두 곳에 있었다(리뷰가 짚었다).
      for (const s of g.soldiers) {
        if (seen.has(s.uid)) continue;
        seen.add(s.uid);
        army.soldiers.push(s);
      }
      delete realm.garrisons[id];
      continue;
    }
    g.soldiers = g.soldiers.filter((s) => {
      if (seen.has(s.uid)) return false;
      seen.add(s.uid);
      return true;
    });
    const st = g.captain && known.has(g.captain) ? army.companions.find((c) => c.id === g.captain) : undefined;
    if (st && st.garrison === undefined) st.garrison = id;
    else g.captain = null;
    if (g.soldiers.length === 0 && g.captain === null) delete realm.garrisons[id];
  }
  army.soldiers.sort((a, b) => a.uid - b.uid);
  if (Object.keys(realm.garrisons).length === 0) delete realm.garrisons;
}

export function clearArmy(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    // 무시한다.
  }
}
